import api from '../../../shared/services/utils/api/api.service';
import errorHelper from '../../../shared/services/utils/api/error-helper.service';
import { ErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { OccupancyAndAverageDailyRate } from '../../../shared/models/kpis.model';
import { OccupancyDatasetModel } from '../../../shared/models/occupancy-dashboard.model';

const MODULE_NAME = 'admin-dashboard';

const fail = (response: unknown, name: string): ErrorInfo => {
  const res = response as { status?: number; data?: { errorCode?: string }; problem?: unknown };
  return errorHelper(
    Number(res.status),
    res.data && res.data.errorCode ? String(res.data.errorCode || '') : String(res.problem || ''),
    MODULE_NAME,
    name
  );
};

export interface MonthKpiUiModel {
  month: string;
  occupancy: number;
  adr: number;
  revpar: number;
  revenue: number;
}

export interface DayPointUiModel {
  day: string;
  revenue: number;
  bookings: number;
}

export interface ProjectOccupancyUiModel {
  name: string;
  occupied: number;
  units: number;
}

export interface OccupancyInsightsUiModel {
  days: DayPointUiModel[];
  projects: ProjectOccupancyUiModel[];
  projectsMonth: ProjectOccupancyUiModel[];
  channels: { name: string; bookings: number; revenue: number }[];
  bookedThisMonth: number;
  canceledThisMonth: number;
  unitsOutOfOrder: number;
}

const iso = (d: Date) => {
  const z = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};

const addDays = (d: Date, n: number) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
};

/** This month first, then last month. */
export function getMonthKpis(): Promise<MonthKpiUiModel[] | ErrorInfo> {
  return api.getOccupancyAndAverageDailyRate(2, { query: {} }).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return (response.data.data as unknown as OccupancyAndAverageDailyRate[]).map((m) => ({
        month: m.month,
        occupancy: m.rate,
        adr: m.ADR_EGP,
        revpar: m.RPAR_EGP,
        revenue: m.MonthlyRevenueEGP
      }));
    }
    return fail(response, 'getMonthKpis');
  });
}

export function mapOccupancyInsights(
  data: OccupancyDatasetModel,
  today: string
): OccupancyInsightsUiModel {
  const monthStart = today.slice(0, 8) + '01';
  const live = data.stays.filter((s) => !s.canceledOn);
  const days: DayPointUiModel[] = [];
  for (
    let d = new Date(data.window.from);
    iso(d) < data.window.to && iso(d) <= today;
    d = addDays(d, 1)
  ) {
    const day = iso(d);
    days.push({
      day,
      revenue: live.reduce(
        (sum, s) =>
          sum +
          s.revenue
            .filter((r) => r.from <= day && day < r.to)
            .reduce((a, r) => a + r.perNightEgp, 0),
        0
      ),
      bookings: data.stays.filter((s) => s.bookedOn.slice(0, 10) === day).length
    });
  }

  const occupiedToday = new Set(
    live.filter((s) => s.from <= today && today < s.to).map((s) => s.roomId)
  );
  const listedToday = data.units.filter((u) =>
    u.listed.some((r) => r.from <= today && today < r.to)
  );
  const byProject: Record<string, { occupied: number; units: number }> = {};
  listedToday.forEach((u) => {
    const p = (byProject[u.project] = byProject[u.project] || { occupied: 0, units: 0 });
    p.units += 1;
    if (occupiedToday.has(u.roomId)) p.occupied += 1;
  });

  const staysByRoom: Record<string, typeof live> = {};
  live.forEach((s) => (staysByRoom[s.roomId] = (staysByRoom[s.roomId] || []).concat(s)));
  const monthDays: string[] = [];
  for (let d = new Date(monthStart); iso(d) <= today; d = addDays(d, 1)) monthDays.push(iso(d));
  const byProjectMonth: Record<string, { occupied: number; units: number }> = {};
  data.units.forEach((u) => {
    const stays = staysByRoom[u.roomId] || [];
    monthDays.forEach((day) => {
      if (!u.listed.some((r) => r.from <= day && day < r.to)) return;
      const p = (byProjectMonth[u.project] = byProjectMonth[u.project] || {
        occupied: 0,
        units: 0
      });
      p.units += 1;
      if (stays.some((s) => s.from <= day && day < s.to)) p.occupied += 1;
    });
  });

  const bookedThisMonth = data.stays.filter((s) => s.bookedOn.slice(0, 10) >= monthStart);
  const byChannel: Record<string, { bookings: number; revenue: number }> = {};
  bookedThisMonth
    .filter((s) => !s.canceledOn)
    .forEach((s) => {
      const c = (byChannel[s.channel || 'Other'] = byChannel[s.channel || 'Other'] || {
        bookings: 0,
        revenue: 0
      });
      c.bookings += 1;
      c.revenue += s.revenue.reduce((sum, r) => {
        const nights = Math.round((new Date(r.to).getTime() - new Date(r.from).getTime()) / 864e5);
        return sum + nights * r.perNightEgp;
      }, 0);
    });

  return {
    days,
    projects: Object.entries(byProject)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.units - a.units),
    projectsMonth: Object.entries(byProjectMonth).map(([name, v]) => ({ name, ...v })),
    channels: Object.entries(byChannel)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.bookings - a.bookings),
    bookedThisMonth: bookedThisMonth.length,
    canceledThisMonth: bookedThisMonth.filter((s) => s.canceledOn).length,
    unitsOutOfOrder: new Set(
      data.blocks
        .filter((b) => b.isMaintenance && b.from <= today && today < b.to)
        .map((b) => b.roomId)
    ).size
  };
}

/** The last 30 days through today, plus everything booked since the 1st. */
export function getOccupancyInsights(): Promise<OccupancyInsightsUiModel | ErrorInfo> {
  const now = new Date();
  const today = iso(now);
  const from =
    iso(addDays(now, -29)) < today.slice(0, 8) + '01'
      ? iso(addDays(now, -29))
      : today.slice(0, 8) + '01';
  return api.getOccupancyDashboardDataset({ from, to: iso(addDays(now, 1)) }).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return mapOccupancyInsights(response.data.data, today);
    }
    return fail(response, 'getOccupancyInsights');
  });
}

export function getOverdueBalance(): Promise<number | ErrorInfo> {
  return api.getOverduePayments({ offset: 1, limit: 1 }).then((response) => {
    if (response.ok && response.data) {
      return response.data.totals?.remainingEgpEquivalent ?? 0;
    }
    return fail(response, 'getOverdueBalance');
  });
}

export function getDoubleBookingsCount(): Promise<number | ErrorInfo> {
  return api.getDoubleBookings().then((response) => {
    if (response.ok && response.data && response.data.data) {
      return response.data.data.length;
    }
    return fail(response, 'getDoubleBookingsCount');
  });
}

export function getOpenHousekeepingToday(): Promise<{ total: number; open: number } | ErrorInfo> {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return api
    .searchHousekeepingOrders({
      offset: 1,
      limit: 200,
      orderDateFrom: start.getTime(),
      orderDateTo: end.getTime()
    })
    .then((response) => {
      if (response.ok && response.data && response.data.data) {
        const orders = response.data.data as { status?: string }[];
        return { total: orders.length, open: orders.filter((o) => o.status !== 'done').length };
      }
      return fail(response, 'getOpenHousekeepingToday');
    });
}

export interface OverdueRowUiModel {
  id: number;
  guestName: string;
  reference: string;
  daysOverdue: number;
  remainingEgp: number;
}

export function getTopOverdue(limit = 6): Promise<OverdueRowUiModel[] | ErrorInfo> {
  return api
    .getOverduePayments({ offset: 1, limit, sortBy: 'remaining', sortDirection: 'desc' })
    .then((response) => {
      if (response.ok && response.data && response.data.data) {
        return response.data.data.map((row) => ({
          id: row.paymentId,
          guestName: row.guestName,
          reference: row.referenceId,
          daysOverdue: row.daysOverdue,
          remainingEgp: row.remainingEgpEquivalent
        }));
      }
      return fail(response, 'getTopOverdue');
    });
}

export interface HousekeepingTodayUiModel {
  id: string;
  unit: string;
  project: string;
  status: string;
  assignee: string;
}

export function getHousekeepingToday(): Promise<HousekeepingTodayUiModel[] | ErrorInfo> {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return api
    .searchHousekeepingOrders({
      offset: 1,
      limit: 50,
      orderDateFrom: start.getTime(),
      orderDateTo: end.getTime()
    })
    .then((response) => {
      if (response.ok && response.data && response.data.data) {
        return (response.data.data as Record<string, any>[]).map((o) => ({
          id: String(o.id),
          unit: o.unitShortName || o.unitName || '',
          project: o.projectName || '',
          status: o.status || '',
          assignee: o.assignedToUserName || ''
        }));
      }
      return fail(response, 'getHousekeepingToday');
    });
}

export interface GuestCallUiModel {
  id: string;
  guestName: string;
  roomName: string;
  checkIn?: number;
  outcome?: string;
}

export function getGuestCalls(): Promise<GuestCallUiModel[] | ErrorInfo> {
  return api.getCxQueue().then((response) => {
    if (response.ok && response.data && response.data.data) {
      const items: Record<string, any>[] = Array.isArray(response.data.data)
        ? response.data.data
        : response.data.data.data || [];
      return items.map((r) => ({
        id: String(r.id),
        guestName: r.primaryGuestName || '',
        roomName: r.roomName || '',
        checkIn: r.checkIn,
        outcome: r.outcomeValue
      }));
    }
    return fail(response, 'getGuestCalls');
  });
}
